import { Flex, TextField, FormField, TextArea, Link, Text } from '@scale/scaleui-radix';
import {
  WebinarError,
  WebinarErrorMessages,
} from 'react-scale/components/WebinarSessions/constants';
import { SingleProjectAutoComplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/SingleProjectAutocomplete';

interface WebinarSettingsFormProps {
  name: string | null;
  setName: (name: string | null) => void;
  description: string | null;
  setDescription: (description: string | null) => void;
  project: string | null;
  setProject: (project: string | null) => void;
  rsvpMax: number | null;
  setRsvpMax: (rsvpMax: number | null) => void;
  minAttendanceDuration: number | null;
  setMinAttendanceDuration: (minAttendanceDuration: number | null) => void;
  expectedNumberOfAttendees: number | null;
  setExpectedNumberOfAttendees: (expectedNumberOfAttendees: number | null) => void;
  webinarErrors: WebinarError[];
  duration: number | null;
  isProjectView: boolean;
}

const WebinarSettingsForm = ({
  name,
  setName,
  description,
  setDescription,
  project,
  setProject,
  rsvpMax,
  setRsvpMax,
  minAttendanceDuration,
  setMinAttendanceDuration,
  expectedNumberOfAttendees,
  setExpectedNumberOfAttendees,
  webinarErrors,
  duration,
  isProjectView,
}: WebinarSettingsFormProps) => {
  return (
    <Flex direction="column" gap="4">
      <FormField.Root size="2">
        <FormField.Label htmlFor="webinar-name-label">Webinar name</FormField.Label>
        <TextField.Root
          id="webinar-name-text-field"
          placeholder="Enter a name for your webinar"
          value={name ?? ''}
          onChange={e => setName(e.target.value)}
        />
        {webinarErrors.includes(WebinarError.NAME_REQUIRED) && (
          <FormField.Description color="error">
            {WebinarErrorMessages[WebinarError.NAME_REQUIRED]}
          </FormField.Description>
        )}
      </FormField.Root>

      <FormField.Root size="2">
        <FormField.Label htmlFor="webinar-description-label" required={false}>
          Description
        </FormField.Label>
        <TextArea
          id="webinar-description-text-area"
          placeholder="Enter a description for your webinar"
          value={description ?? ''}
          onChange={e => {
            const value = e.target.value;
            setDescription(value === '' ? null : value);
          }}
        />
      </FormField.Root>

      <FormField.Root size="2">
        <FormField.Label htmlFor="webinar-project-label" required={false}>
          Project
        </FormField.Label>
        <FormField.Description>
          <ProjectFieldSubtitle isProjectView={isProjectView} />
        </FormField.Description>
        <SingleProjectAutoComplete
          projectId={project ?? ''}
          onProjectIdChange={setProject}
          disabled={isProjectView}
        />
      </FormField.Root>

      <FormField.Root size="2">
        <FormField.Label htmlFor="webinar-rsvp-limit-label" required={false}>
          RSVP limit
        </FormField.Label>
        <TextField.Root
          id="webinar-rsvp-limit-text-field"
          placeholder="Enter a number"
          value={rsvpMax ?? ''}
          onChange={e => {
            const value = e.target.value;
            setRsvpMax(value === '' ? null : Number(value));
          }}
          type="number"
          min={1}
          step={1}
        />
      </FormField.Root>

      <FormField.Root size="2">
        <FormField.Label htmlFor="webinar-min-attendance-duration-label" required={false}>
          Minimum attendance duration (in minutes)
        </FormField.Label>
        <TextField.Root
          id="webinar-min-attendance-duration-text-field"
          placeholder="Enter a number"
          value={minAttendanceDuration ?? ''}
          onChange={e => {
            const value = e.target.value;
            setMinAttendanceDuration(value === '' ? null : Number(value));
          }}
          type="number"
          min={0}
          max={duration ?? 0}
          step={1}
        />
      </FormField.Root>
      <FormField.Root size="2">
        <FormField.Label htmlFor="webinar-expected-attendees-label" required={false}>
          Expected number of attendees
        </FormField.Label>
        <TextField.Root
          id="webinar-expected-attendees-text-field"
          placeholder="Enter a number"
          value={expectedNumberOfAttendees ?? ''}
          onChange={e => {
            const value = e.target.value;
            setExpectedNumberOfAttendees(value === '' ? null : Number(value));
          }}
          type="number"
          min={0}
          step={1}
        />
      </FormField.Root>
    </Flex>
  );
};

const ProjectFieldSubtitle = ({ isProjectView }: { isProjectView: boolean }) => {
  return (
    <Text>
      If a project is added to this webinar, contributors will need to be assigned to the project to
      see it in Outlier. Project-agnostic webinars will be visible to all invited contributors.
      {isProjectView && (
        <>
          {
            ' If you do not want your webinar to be only accessible to people on the current project, please create your webinar '
          }
          <Link
            href="/corp/genai-ops-hub/webinars/create"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </Link>
          {' instead'}
        </>
      )}
    </Text>
  );
};

export default WebinarSettingsForm;
